/**
 * Board conflicts (BOARD-CONFLICTS-01) — the at-a-glance "this call is on the
 * wrong tech" signal shown directly on the board cards (not just in the route
 * recommendations). Reuses the same detectors as the recommendation engine:
 *   - excluded violation: the call is on a tech the notes say to AVOID,
 *   - requested mismatch: the call names a tech but is on someone else,
 *   - skill mismatch: the assigned tech isn't flagged for a skill the call needs,
 *   - requested-here: a soft "lock" badge when the call IS on its requested tech.
 *
 * Computed once for the whole board (gated on the skills load so it never false-
 * flags) and shared via context so each `DispatchBlock` can read its own without
 * prop-drilling or recomputing.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Dispatch } from "@/lib/types";
import {
  isPseudoDispatch,
  useTechSkills,
  useLocationNotes,
  useActiveTechs,
} from "./api";
import {
  deriveRequiredSkills,
  deriveRequestedTech,
  deriveExcludedTechs,
  skillName,
} from "@/lib/recommendations";

export interface BoardConflict {
  tone: "warn" | "lock";
  short: string; // chip text
  detail: string; // tooltip
}

export function useBoardConflicts(
  rows: Dispatch[],
  /** empnos marked OUT (all-day availability event) on the board date. */
  outEmpnos: string[] = [],
): Map<string, BoardConflict> {
  const skills = useTechSkills().data;
  const baseRows = rows.filter((r) => !isPseudoDispatch(r));
  const locationNotes = useLocationNotes(baseRows.map((r) => r.custno)).data;
  const rosterTechs = useActiveTechs("03").data;

  const baseSig = baseRows.map((r) => `${r.dispatch}:${r.tech_empno}`).join(",");
  const locSig = Object.keys(locationNotes ?? {}).sort().join(",");
  const rosterSig = (rosterTechs ?? []).map((t) => t.empno).join(",");
  const outSig = [...outEmpnos].sort().join(",");

  return useMemo(() => {
    const map = new Map<string, BoardConflict>();
    if (!skills) return map; // gate on skills so we never false-flag
    const outSet = new Set(outEmpnos);

    // CONFLICTS-CREW (Seth 2026-09-01, Wendy McMahon's 2pm: "Why is it
    // displaying not qualified…?"): a multi-tech call (same dispatch, several
    // counters) is covered when ANY tech on it has the needed skill — Andrew
    // riding along on Jeremy's mini-split call is not a conflict. Build the
    // crew per dispatch first.
    const crewOf = new Map<string, Set<string>>();
    for (const r of baseRows) {
      const set = crewOf.get(r.dispatch) ?? new Set<string>();
      set.add(r.tech_empno);
      crewOf.set(r.dispatch, set);
    }

    const info = new Map<string, { name: string; first: string }>();
    for (const t of rosterTechs ?? [])
      info.set(t.empno, {
        name: t.empname ?? `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim(),
        first: (t.firstname ?? "").trim(),
      });
    for (const r of baseRows)
      if (!info.has(r.tech_empno))
        info.set(r.tech_empno, {
          name:
            r.tech_name ?? `${r.tech_first ?? ""} ${r.tech_last ?? ""}`.trim(),
          first: (r.tech_first ?? "").trim(),
        });
    const nameOf = (e: string) => info.get(e)?.name || e;
    const firstOf = (e: string) => info.get(e)?.first || nameOf(e);
    const roster = [...info].map(([empno, v]) => ({ empno, first: v.first }));
    const skillsOf = (e: string) => new Set(skills[e] ?? []);

    for (const r of baseRows) {
      const tech = r.tech_empno;
      // CONFLICTS-CREW: key per dispatch AND counter — before this, both techs
      // on a two-man call shared one map slot, so Andrew's flag painted
      // Jeremy's card too (and named the wrong man in the tooltip).
      const key = `${r.dispatch}|${r.counter ?? "000"}`;
      const locNote = locationNotes?.[`${r.custno}|${r.locno}`] ?? "";
      const notes = `${r.notes ?? ""}\n${locNote}`;
      const excluded = deriveExcludedTechs(notes, roster);
      // null when the call is already on an acceptable tech ("Jeremy or Camron").
      let requested = deriveRequestedTech(notes, roster, tech);
      if (requested && excluded.includes(requested)) requested = null;
      // A skill is missing only when NOBODY on the call carries it.
      const crew = crewOf.get(r.dispatch) ?? new Set([tech]);
      const need = deriveRequiredSkills(r.notes).filter(
        (s) => ![...crew].some((e) => skillsOf(e).has(s)),
      );

      if (outSet.has(tech)) {
        map.set(key, {
          tone: "warn",
          short: "Tech is out",
          detail: `${nameOf(tech)} is marked out today — this call needs a new home.`,
        });
      } else if (excluded.includes(tech)) {
        map.set(key, {
          tone: "warn",
          short: "Wrong tech",
          detail: `Notes ask NOT to send ${nameOf(tech)}.`,
        });
      } else if (requested && requested !== tech) {
        map.set(key, {
          tone: "warn",
          short: `Wants ${firstOf(requested)}`,
          detail: `Customer requested ${nameOf(requested)}, not ${nameOf(tech)}.`,
        });
      } else if (need.length) {
        // Softer + actionable (Seth: "Not qualified… doesn't feel right or
        // helpful"): name the skill in the chip, and say how to clear it.
        const names = need.map(skillName).join(", ");
        const who =
          crew.size > 1
            ? `nobody on this call (${[...crew].map(nameOf).join(", ")}) is flagged for ${names}`
            : `${nameOf(tech)} isn't flagged for ${names}`;
        map.set(key, {
          tone: "warn",
          short: need.length === 1 ? `${skillName(need[0])}?` : "Skill check",
          detail: `The notes look like ${names} work and ${who}. If that's actually fine, flag the skill under Settings → Team and this clears.`,
        });
      } else if (requested === tech) {
        map.set(key, {
          tone: "lock",
          short: `${firstOf(tech)} only`,
          detail: `Customer requested ${nameOf(tech)} — keep it here.`,
        });
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skills, baseSig, locSig, rosterSig, outSig]);
}

const BoardConflictsContext = createContext<Map<string, BoardConflict>>(
  new Map(),
);

export function BoardConflictsProvider({
  conflicts,
  children,
}: {
  conflicts: Map<string, BoardConflict>;
  children: ReactNode;
}) {
  return (
    <BoardConflictsContext.Provider value={conflicts}>
      {children}
    </BoardConflictsContext.Provider>
  );
}

export function useConflict(
  dispatch: string,
  counter?: number | string | null,
): BoardConflict | undefined {
  return useContext(BoardConflictsContext).get(
    `${dispatch}|${counter ?? "000"}`,
  );
}
